/**
 * CompactDropArm — the compact Visual DDL body for a parked top-level `DROP`.
 *
 * A DROP has no structure to edit visually, so this arm stays honest: it
 * repeats whatever facts the structural extractor can give for the head
 * (`DROP INDEX … ON t`), a one-line summary of what the arm IS for
 * (`vqeCopy.ddlCard.dropCapability` — the read-only card keeps its own
 * "structural editing isn't here" wording, which would only restate a
 * limitation here), and adds the one action that IS useful before running a
 * drop — `Check dependents`, which hands the parked object to the existing
 * Dependency Viewer workspace (`dependents/openInDependencyViewer`). That
 * button is portalled into the popover's single pinned action row via
 * `FormActionsSlot`, so it shares one row with `Edit as SQL`.
 *
 * Naming the dropped object: `extractDdlStructure` covers only a few DROP
 * heads (index / trigger / routine), so the target comes from the statement's
 * `schemaEffects` — the shared grammar-level extractor that DOES name
 * `DROP TABLE` / `DROP VIEW` / … . When neither can name it (or no connection
 * is bound) the button is disabled with a specific-reason tooltip rather than
 * silently doing nothing.
 *
 * Redesign: .superpowers/sdd/2026-07-28-compact-visual-ddl-editor/ (T3).
 */
import { Network } from 'lucide-react';

import { Button } from '../../../../components/ui/button';
import { useAnalytics } from '../../../../core/analytics/useAnalytics';
import type { SchemaObjectKind } from '../../../sql-grammar/model';
import { factsFromStructure } from '../../visual-query-editor/compact/compactDdlCard.facts';
import { VqeTooltip } from '../../visual-query-editor/render/common/VqeTooltip';
import { vqeCopy } from '../../visual-query-editor/ux/vqeCopy';
import type { DependencyObjectType } from '../dependencies/types';
import { openInDependencyViewer } from '../dependents/openInDependencyViewer';
import type { VqeDdlEditContext } from '../edit/editContext';
import { FormActionsSlot } from '../render/common/FormActionsSlot';
import { structureFromDdlEditContext } from './structureFromDdlEditContext';

const COPY = {
  action: 'Check dependents',
  tooltip: 'Open this object in the Dependency Viewer before you drop it',
  unavailable:
    'Dependents aren’t resolvable for this statement — open the object from the Object Explorer’s View Dependencies action instead',
  noConnection: 'Check dependents · no database is connected',
} as const;

/** `SchemaObjectKind` → the Dependency Viewer's own object taxonomy. Kinds it
 *  has no arm for degrade to `'unknown'`, which the viewer renders as a
 *  generic object rather than mislabelling it. */
const DEPENDENCY_TYPE_BY_SCHEMA_KIND: Partial<Record<SchemaObjectKind, DependencyObjectType>> = {
  table: 'table',
  view: 'view',
  index: 'index',
  routine: 'procedure',
  trigger: 'trigger',
  synonym: 'synonym',
};

type DropTarget = {
  readonly schema: string | null;
  readonly objectName: string;
  readonly objectType: DependencyObjectType;
};

function dropTargetFromContext(editContext: VqeDdlEditContext): DropTarget | null {
  const buffer = editContext.service.getParsedBuffer(editContext.documentId);
  const statement = buffer?.documentStructure.statements.find(
    (candidate) => candidate.id === editContext.statementId,
  );
  const effect = statement?.schemaEffects?.find((candidate) => candidate.effect === 'drop');
  if (!effect?.target?.name) return null;
  return {
    schema: effect.target.schema ?? null,
    objectName: effect.target.name,
    objectType: DEPENDENCY_TYPE_BY_SCHEMA_KIND[effect.objectKind] ?? 'unknown',
  };
}

export interface CompactDropArmProps {
  readonly editContext: VqeDdlEditContext;
}

export function CompactDropArm({ editContext }: CompactDropArmProps) {
  const { track } = useAnalytics();
  const structure = structureFromDdlEditContext(editContext);
  const facts = structure ? factsFromStructure(structure) : [];
  const target = dropTargetFromContext(editContext);
  const connected = !!editContext.connectionId && !!editContext.database;
  const disabled = !target || !connected;
  const tooltip = !target ? COPY.unavailable : !connected ? COPY.noConnection : COPY.tooltip;

  const handleCheckDependents = () => {
    if (!target || !connected) return;
    // Reuses the DDL card's existing event (no new event name) with its own
    // `action` value — see global-constraints "Analytics".
    track('feature:vqe_ddl_card_action', { action: 'check-dependents', objectKind: 'drop' });
    openInDependencyViewer({
      connectionId: editContext.connectionId,
      database: editContext.database,
      schema: target.schema,
      objectName: target.objectName,
      objectType: target.objectType,
    });
  };

  return (
    <div className="flex flex-col gap-3" data-testid="compact-ddl-drop-body">
      {facts.length > 0 ? (
        <ul className="flex flex-col gap-1 text-xs text-muted-foreground">
          {facts.map((fact, index) => (
            <li key={index} data-testid="vqe-compact-ddl-fact">
              {fact}
            </li>
          ))}
        </ul>
      ) : null}

      <p className="text-xs text-muted-foreground" data-testid="vqe-compact-ddl-capability">
        {vqeCopy.ddlCard.dropCapability}
      </p>

      {/* Portalled into the popover's single pinned action row (next to
          Edit as SQL) when the compact shell claims the slot; rendered in
          place for any host that doesn't. */}
      <FormActionsSlot className="flex items-center gap-2">
        <VqeTooltip content={tooltip}>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="h-7 gap-1 px-2 text-xs"
            onClick={handleCheckDependents}
            disabled={disabled}
            aria-label={COPY.action}
            data-testid="compact-ddl-check-dependents"
          >
            <Network className="h-3.5 w-3.5" />
            {COPY.action}
          </Button>
        </VqeTooltip>
      </FormActionsSlot>
    </div>
  );
}
